import { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import httpclient from "../../../service/httpclient";
import { composeLotterySharePoster } from "./lottery-utils";

const POSTER_FILE_NAME = "icdesign-题库Pro分享海报.png";

function isLikelyMobileDevice() {
  return Boolean(
    window.matchMedia?.("(pointer: coarse)")?.matches ||
      window.matchMedia?.("(max-width: 599px)")?.matches
  );
}

function canvasToBlob(canvas) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/png");
  });
}

function downloadBlob(blob) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = POSTER_FILE_NAME;
  link.click();
  window.URL.revokeObjectURL(url);
}

function useLotterySharePoster() {
  const [poster, setPoster] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!poster?.qr_code) {
      setPosterPreview(null);
      return;
    }

    let cancelled = false;
    composeLotterySharePoster(poster.qr_code, 0.24)
      .then((canvas) => {
        if (!cancelled) {
          setPosterPreview(canvas.toDataURL("image/jpeg", 0.92));
        }
      })
      .catch(() => {
        if (!cancelled) setPosterPreview(null);
      });

    return () => {
      cancelled = true;
    };
  }, [poster]);

  const open = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      setPosterPreview(null);
      const data = await httpclient.createLotterySharePoster();
      setPoster(data);
    } finally {
      setLoading(false);
    }
  }, [loading]);

  const close = useCallback(() => {
    setPoster(null);
  }, []);

  const download = useCallback(async () => {
    if (!poster?.qr_code) return;
    const canvas = await composeLotterySharePoster(poster.qr_code, 1);
    const blob = await canvasToBlob(canvas);
    if (!blob) return;

    const file = new File([blob], POSTER_FILE_NAME, { type: "image/png" });
    const canSharePoster =
      isLikelyMobileDevice() &&
      navigator.canShare?.({ files: [file] }) &&
      navigator.share;

    if (canSharePoster) {
      try {
        await navigator.share({
          title: "IC Design 题库Pro会员分享海报",
          text: "分享 IC Design 题库Pro会员活动海报",
          files: [file],
        });
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }

    downloadBlob(blob);
    if (isLikelyMobileDevice()) {
      message.info("如未自动保存到相册，可长按海报图片保存");
    }
  }, [poster]);

  const copyLink = useCallback(async () => {
    const shareUrl = poster?.share_url;
    if (!shareUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const input = document.createElement("input");
        input.value = shareUrl;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      message.success("分享链接已复制");
    } catch (e) {
      message.error("复制失败，请手动复制链接");
    }
  }, [poster]);

  return {
    poster,
    posterPreview,
    loading,
    open,
    close,
    download,
    copyLink,
  };
}

export default useLotterySharePoster;
